import { useEffect, useState } from 'react';
import { SkeletonCard, CharacterCard } from '@client/components';
import { Typography, Box, useMediaQuery, useTheme, Grid } from '@mui/material';
import { Character } from '@shared-types'
import { AnimatePresence, motion } from 'framer-motion';

interface CharacterListProps {
  characters: Character[];
  loading: boolean;
  initialLoaded: boolean;
  itemsPerPage: number;
  t: (key: string, options?: Record<string, any>) => string;
}

export function CharacterList({
  characters,
  loading,
  initialLoaded,
  itemsPerPage,
  t,
}: CharacterListProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showSkeleton, setShowSkeleton] = useState(true);

  useEffect(() => {
    if (!loading && initialLoaded) {
      const timeout = setTimeout(() => setShowSkeleton(false), 300);
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [loading, initialLoaded]);

  const skeletonCount = isMobile ? Math.min(4, itemsPerPage) : itemsPerPage;

  return (
    <Grid
      container
      spacing={2}
      sx={{
        position: 'relative',
      }}
    >
      <AnimatePresence mode="wait">
        {showSkeleton ? (
          <motion.div
            key="skeletons"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={{ display: 'contents' }}
          >
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <Grid key={`skeleton-${i}`} size={{ xs: 6, sm: 4, md: 3, lg: 2.4, xl: 2 }}>
                <SkeletonCard />
              </Grid>
            ))}
          </motion.div>
        ) : initialLoaded && characters.length > 0 ? (
          <motion.div
            key="characters"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={{ display: 'contents' }}
          >
            {characters.map((char) => (
              <Grid key={char.id} size={{ xs: 6, sm: 4, md: 3, lg: 2.4, xl: 2 }}>
                <CharacterCard character={char} />
              </Grid>
            ))}
          </motion.div>
        ) : initialLoaded ? (
          <motion.div
            key="no-characters"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              color: theme.palette.text.secondary,
              textAlign: 'center',
              width: '100%',
            }}
          >
            <Grid size={12}>
              <Typography variant="body1">
                {t('messages.noCharactersFound')}
              </Typography>
            </Grid>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Grid>
  );
}